import Flex from 'UIKit/Box/Flex'
import { FlexProps } from 'UIKit/Box/types'
import TransText from 'UIKit/Text/TransText'
import { TextProps } from 'UIKit/Text/types'
import { Game } from 'config/types/game'
import { ColumnCenter } from 'layout/Components/Column'
import { RowCenter } from 'layout/Components/Row'
import styled from 'styled-components'
import { Icons } from 'svgs'
import { colors } from 'theme/colors'
import { useRequest } from 'hooks/useRequest'
import { useAuth } from 'state/auth/hooks'
import { useActiveBonusInWageringProcess } from 'state/bonus/hooks'
import { useEffect, useMemo, useState } from 'react'
import GameService from 'services/GameService'
import GameItem from './GameItem'

interface NotAvailableGameDesktopOverlayProps {
  isBlocked?: boolean
  isRestricted?: boolean
  iconSize?: number
}

const NotAvailableGameDesktopOverlay: React.FC<NotAvailableGameDesktopOverlayProps & FlexProps & TextProps> = ({
  isBlocked = false,
  isRestricted = false,
  iconSize = 100,
  ...props
}) => {
  const { execute } = useRequest()
  const { isSigned } = useAuth()
  const bonusWagering = useActiveBonusInWageringProcess()

  const [recommendGamesForCurrentWageringBonus, setRecommendGamesForCurrentWageringBonus] = useState<Game[]>([])

  const isCreditBonusActive = useMemo(
    () => isSigned && bonusWagering && bonusWagering.isCreaditReward,
    [isSigned, bonusWagering],
  )

  useEffect(() => {
    const getRecommendGamesForCurrentWageringBonus = async () => {
      const response = await execute(GameService.getGames({ gameCollectionId: bonusWagering.gameCollectionId }))

      if (response?.data) {
        setRecommendGamesForCurrentWageringBonus(response.data.items)
      }
    }

    if (isCreditBonusActive && isRestricted) {
      getRecommendGamesForCurrentWageringBonus()
    }
  }, [isCreditBonusActive, isRestricted])

  const getRandomItems = (arr: Game[], randomItem: number) => {
    const result: Game[] = []
    const itemsCopy = [...arr]

    for (let i = 0; i < randomItem; i++) {
      const randomIndex = Math.floor(Math.random() * itemsCopy.length)
      result.push(itemsCopy.splice(randomIndex, 1)[0])
    }

    return result
  }

  const recommendGameForCurrentWageringBonusList = getRandomItems(recommendGamesForCurrentWageringBonus, 5)

  return (
    <StyledOverlayWrapper {...props}>
      <>
        {isBlocked ? (
          <ColumnCenter maxWidth="320px">
            <Icons.LockIcon width={iconSize} fill={colors.textTertiary} />
            <TransText fontSize="32px" textAlign="center" mt="8px" {...props}>
              This game is unavailable in your region
            </TransText>
          </ColumnCenter>
        ) : isRestricted ? (
          <ColumnCenter maxWidth="100%" maxHeight="100%">
            <ColumnCenter maxWidth="570px">
              <Icons.LockIcon width={iconSize} fill={colors.textTertiary} />
              <TransText fontSize="24px" textAlign="center" mt="8px" {...props}>
                This game is restricted while your Claim to Play Bonus is active.
              </TransText>
              <TransText fontSize="24px" textAlign="center" mt="8px" {...props}>
                Check out these other games instead!
              </TransText>
            </ColumnCenter>

            <Flex mt="50px" p="0 20px">
              {recommendGameForCurrentWageringBonusList?.map((game) => {
                if (!game) return

                return (
                  <Flex flex={1} maxWidth={200} maxHeight={200} width="100%">
                    <GameItem game={game} large={false} />
                  </Flex>
                )
              })}
            </Flex>
          </ColumnCenter>
        ) : (
          <ColumnCenter maxWidth="320px">
            <Icons.SettingConfigIcon width={iconSize} fill={colors.textTertiary} />
            <TransText fontSize="32px" textAlign="center" mt="8px" {...props}>
              This game is under maintenance
            </TransText>
          </ColumnCenter>
        )}
      </>
    </StyledOverlayWrapper>
  )
}

const StyledOverlayWrapper = styled(RowCenter)`
  z-index: 2;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  position: absolute;
  opacity: 0;
  background: linear-gradient(180deg, rgba(22, 28, 36, 0) -22.78%, #191d25e0 55%, #17181b 86.75%);
  opacity: 1;
`

export default NotAvailableGameDesktopOverlay
